// Hero protocol graph — pure star: apps orbit around furtif, particles flow both ways.
// No app↔app edges: the protocol is the hub, apps don't talk to each other.

interface AppNode {
  id: 'apple' | 'deezer' | 'soundcloud' | 'youtube';
  ang: number;
  label: string[];
  code: string;
  size: number;
  solid: boolean;
}

interface Particle {
  from: AppNode['id'];
  dir: 'in' | 'out';
  color: string;
  r: number;
  dur: number;
  delay: number;
}

const C = 290;
const R = 200;

const APPS: AppNode[] = [
  { id: 'apple',      ang: -60, label: ['apple', 'music'], code: 'A1', size: 34, solid: true },
  { id: 'deezer',     ang:  30, label: ['deezer'],         code: 'A2', size: 34, solid: true },
  { id: 'soundcloud', ang: 120, label: ['sound', 'cloud'], code: 'A3', size: 34, solid: true },
  { id: 'youtube',    ang: 210, label: ['youtube'],        code: 'A4', size: 34, solid: true },
];

const PARTICLES: Particle[] = [
  { from: 'apple',      dir: 'in',  color: '#A4BB85', r: 2.5, dur: 3.2, delay: -0.6 },
  { from: 'apple',      dir: 'out', color: '#E08A6B', r: 2,   dur: 5.1, delay: -2.4 },
  { from: 'deezer',     dir: 'in',  color: '#9CC8D6', r: 3,   dur: 4.0, delay: -1.8 },
  { from: 'deezer',     dir: 'out', color: '#A4BB85', r: 2,   dur: 4.4, delay: -0.3 },
  { from: 'soundcloud', dir: 'in',  color: '#E08A6B', r: 2.5, dur: 3.5, delay: -2.1 },
  { from: 'soundcloud', dir: 'out', color: '#9CC8D6', r: 2,   dur: 5.3, delay: -1.5 },
  { from: 'youtube',    dir: 'in',  color: '#A4BB85', r: 2,   dur: 4.8, delay: -2.8 },
];

function pos(ang: number, r = R): [number, number] {
  const rad = (ang * Math.PI) / 180;
  return [C + Math.cos(rad) * r, C + Math.sin(rad) * r];
}

export function HeroGraph() {
  return (
    <svg className="graph" viewBox="0 0 580 580" fill="none">
      <defs>
        <radialGradient id="halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#E08A6B" stopOpacity=".22" />
          <stop offset="60%"  stopColor="#E08A6B" stopOpacity=".05" />
          <stop offset="100%" stopColor="#E08A6B" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ray" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#E08A6B" stopOpacity=".05" />
          <stop offset="55%"  stopColor="#E08A6B" stopOpacity=".55" />
          <stop offset="100%" stopColor="#E08A6B" stopOpacity=".15" />
        </linearGradient>
      </defs>

      {/* Halo behind hub */}
      <circle cx={C} cy={C} r="220" fill="url(#halo)" />

      {/* Concentric guide rings */}
      <circle cx={C} cy={C} r="90"  stroke="#2A3045" strokeWidth="1" strokeDasharray="2 5" />
      <circle cx={C} cy={C} r={R}   stroke="#2A3045" strokeWidth="1" strokeDasharray="2 5" />
      <circle cx={C} cy={C} r="250" stroke="#2A3045" strokeWidth="1" strokeDasharray="2 5" opacity=".4" />

      {/* Slow rotating orbit ring — gives motion without spinning the apps themselves */}
      <g style={{ transformOrigin: `${C}px ${C}px`, animation: 'graphSpin 60s linear infinite' }}>
        <circle cx={C} cy={C} r={R} stroke="#3A4060" strokeWidth="1" strokeDasharray="1 9" opacity=".7" />
        <circle cx={C + R} cy={C} r="1.5" fill="#9CC8D6" opacity=".7" />
        <circle cx={C - R} cy={C} r="1.5" fill="#E08A6B" opacity=".5" />
        <circle cx={C} cy={C + R} r="1.5" fill="#A4BB85" opacity=".6" />
      </g>

      {/* Rays (app → hub) — only these, no app↔app links */}
      <g strokeWidth="1" strokeLinecap="round">
        {APPS.map((a) => {
          const [x, y] = pos(a.ang);
          return <line key={a.id} x1={x} y1={y} x2={C} y2={C} stroke="url(#ray)" opacity={a.solid ? 0.9 : 0.4} />;
        })}
      </g>

      {/* App nodes */}
      <g fontFamily="Geist Mono">
        {APPS.map((a) => {
          const [x, y] = pos(a.ang);
          return (
            <g key={a.id}>
              <circle
                cx={x} cy={y} r={a.size}
                fill="#181C28"
                stroke="#2A3045"
                strokeDasharray={a.solid ? 'none' : '2 3'}
                opacity={a.solid ? 1 : 0.7}
              />
              {a.label.map((line, i) => (
                <text
                  key={i}
                  x={x}
                  y={y + (a.label.length === 1 ? 2 : i === 0 ? -3 : 8)}
                  textAnchor="middle"
                  fill={a.solid ? '#C9BFA8' : '#8A8270'}
                  fontSize={a.size > 28 ? 9 : 7.5}
                >{line}</text>
              ))}
              <text
                x={x}
                y={y + (a.label.length === 1 ? 14 : 19)}
                textAnchor="middle"
                fill="#5C5648"
                fontSize="7"
                letterSpacing="1"
              >{a.code}</text>
            </g>
          );
        })}
      </g>

      {/* Hub — furtif. Filled disc instead of an inner dot. */}
      <g>
        {/* Pulsing halo rings expanding outward */}
        <circle cx={C} cy={C} r="42" fill="none" stroke="#E08A6B" strokeWidth="1.5" opacity="0.6">
          <animate attributeName="r" values="42;72" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;0" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx={C} cy={C} r="42" fill="none" stroke="#E08A6B" strokeWidth="1.5" opacity="0.5">
          <animate attributeName="r" values="42;72" dur="2.4s" begin="-1.2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0" dur="2.4s" begin="-1.2s" repeatCount="indefinite" />
        </circle>
        {/* Solid disc — pulses size + brightness */}
        <circle cx={C} cy={C} r="42" fill="#E08A6B" stroke="#E08A6B" strokeWidth="1.5">
          <animate attributeName="r" values="40;48;40" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="fill" values="#E08A6B;#F4A88A;#E08A6B" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <text x={C} y={C + 4} textAnchor="middle" fontFamily="Geist Mono" fontSize="11" fontWeight="500" fill="#11141C" letterSpacing="2">furtif</text>
      </g>

      {/* Bidirectional flowing particles — random durations + delays make it feel alive */}
      {PARTICLES.map((p, i) => {
        const app = APPS.find((a) => a.id === p.from);
        if (!app) return null;
        const [ax, ay] = pos(app.ang);
        const path = p.dir === 'in'
          ? `M ${ax} ${ay} L ${C} ${C}`
          : `M ${C} ${C} L ${ax} ${ay}`;
        return (
          <circle key={i} r={p.r} fill={p.color} opacity={0.85}>
            <animateMotion dur={`${p.dur}s`} begin={`${p.delay}s`} repeatCount="indefinite" path={path} />
            <animate attributeName="opacity" values="0;.9;.9;0" keyTimes="0;0.15;0.85;1" dur={`${p.dur}s`} begin={`${p.delay}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
    </svg>
  );
}
